import {
  DueDisplayOptions,
  PlannedOccurrence,
  Task,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { addDays, endOfDay, isAfter, startOfDay } from 'date-fns';

import { getEarliestUncompletedOccurrence } from './get-earliest-uncompleted-occurrence';
import { getTaskPlanning } from './get-task-planning';
import { getActiveSchedule } from './schedules/get-active-schedule';
import { getScheduleTimeframeByDate } from './schedules/get-schedule-timeframe-by-date';

export type GetFutureOccurrenceOptions = {
  plannedOccurrence?: PlannedOccurrence;
  displayDue: DueDisplayOptions[];
  today: Timestamp;
  displayDate?: Timestamp;
  weekStartsOn: WeekDays;
};

export const getFutureOccurrence = (
  task: Task,
  {
    plannedOccurrence,
    displayDue,
    today,
    displayDate,
    weekStartsOn,
  }: GetFutureOccurrenceOptions,
) => {
  if (plannedOccurrence || !displayDue.includes(DueDisplayOptions.Future)) {
    return undefined;
  }
  const dateToMeasureFrom = endOfDay(displayDate ?? today);

  if (
    task.endStrategy?.deadline &&
    isAfter(task.endStrategy.deadline, endOfDay(dateToMeasureFrom))
  ) {
    return getTaskPlanning(task, {
      startDate: startOfDay(dateToMeasureFrom),
      endDate: dateToMeasureFrom,
      today,
      weekStartsOn,
    })[0];
  }

  const activeSchedule = getActiveSchedule(task.schedules, today);
  if (!activeSchedule) {
    return undefined;
  }

  const timeframe = getScheduleTimeframeByDate(
    activeSchedule,
    dateToMeasureFrom,
    weekStartsOn,
  );
  const futureOccurrence = getEarliestUncompletedOccurrence(task, {
    startDate: dateToMeasureFrom,
    endDate: timeframe.endDate,
    today,
    weekStartsOn,
  });
  if (futureOccurrence) {
    return futureOccurrence;
  }

  const nextTimeframe = getScheduleTimeframeByDate(
    activeSchedule,
    addDays(timeframe.endDate, 1),
    weekStartsOn,
  );
  return getEarliestUncompletedOccurrence(task, {
    startDate: nextTimeframe.startDate,
    endDate: nextTimeframe.endDate,
    today,
    weekStartsOn,
  });
};
